// src/pdf/returnsV2PdfSpecs.js
import { downloadPdfFromSpec } from "./pdfCore";
import { listImagesForReturnsV2, getImageDataUrl } from "./spImages";

function bool(v) {
  return v ? "Yes" : "No";
}

function safeFileName(name) {
  return String(name || "report.pdf").replace(/[\/\\:?*"<>|]/g, "_");
}

function reportFileNameFromReturnsV2(row) {
  const f = row.type === "cyl" ? row.cyl?.form : row.parts?.form;
  const rawSret = f?.sretNumber ? String(f.sretNumber).trim() : "";
  const rawRn = f?.returnsNoteNumber ? String(f.returnsNoteNumber).trim() : "";

  const sretPart = rawSret
    ? (rawSret.toUpperCase().startsWith("SRET") ? rawSret : `SRET${rawSret}`)
    : "SRETUNKNOWN";

  const rnPart = rawRn || "RN-UNKNOWN";
  return safeFileName(`${sretPart} - ${rnPart}.pdf`);
}

export async function buildReturnsV2PdfSpec(row, opts = {}) {
  const dt = row.createdAt?.toDate?.();
  const submitted = row.submittedAt?.toDate?.();

  const isCyl = row.type === "cyl";
  const f = isCyl ? row.cyl?.form || {} : row.parts?.form || {};

  const subtitles = [
    dt ? `Created: ${dt.toLocaleString()}` : null,
    submitted ? `Submitted: ${submitted.toLocaleString()}` : null,
    row.status ? `Status: ${row.status}` : null,
    `Doc ID: ${row.id}`,
  ].filter(Boolean);

  const sections = [];

  if (isCyl) {
    const parts = row.cyl?.parts || {};
    const leaks = row.cyl?.leaks || {};
    const c = row.cyl?.cylCheck || {};
    const returnedParts = row.cyl?.returnedParts || [];

    sections.push({
      title: "Return & Cylinder",
      table: {
        rows: [
          ["Returns Note #", f.returnsNoteNumber, "Serial", f.serialNumber],
          ["SRET #", f.sretNumber, "Model", f.cylinderModel],
          ["Capacity", f.capacity, "Manufacture Date", f.dateOfManufacture],
        ],
      },
    });

    sections.push({
      title: "Reasons & Fault Found",
      table: {
        rows: [
          ["Return Reason", f.reasonCode, "Reason Defect", f.reasonDefectCode],
          ["Fault Found", f.faultFoundCode, "Fault Defect", f.faultDefectCode],
        ],
      },
    });

    sections.push({
      title: "Cylinder Checklist",
      table: {
        rows: [
          ["Installed", bool(c.cylinderInstalled), "Damage to Case", bool(c.damageToCase)],
          ["Damage to Packaging", bool(c.damageToPackaging), "Damage to Bosses", bool(c.damageToBosses)],
          ["Damage to Pipework/Parts", bool(c.damageToPipeworkParts), "Damage to Foam", bool(c.damageToFoam)],
          ["Strip & Test", bool(c.stripAndTest), "", ""],
        ],
      },
    });

      const ops = f?.operators || {};
      const hasOps = !!(ops.welded || ops.airTest || ops.hotTested || ops.foamTested);

      if (hasOps) {
        sections.push({
          title: "Operators",
          table: {
            rows: [
              ["Welded", ops.welded || "—", "Air tested", ops.airTest || "—"],
              ["Hot tested", ops.hotTested || "—", "Foam tested", ops.foamTested || "—"],
            ],
          },
        });
      }


    // leaks (dynamic)
    const leakRows = Object.keys(leaks || {})
      .filter((k) => k !== "otherComments")
      .slice(0, 12)
      .map((k) => [k, bool(leaks[k]), "", ""]);
    if (leaks?.other) leakRows.push(["Other Comments", leaks.otherComments || "—", "", ""]);

    sections.push({
      title: "Leak Checklist",
      table: { rows: leakRows.length ? leakRows : [["—", "—", "—", "—"]] },
    });

    sections.push({
      title: "Parts Return",
      table: {
        rows: [
          ["No parts returned", bool(parts.noPartsReturned), "All parts returned", bool(parts.allPartsReturned)],
          ["Partial return", bool(parts.partialReturn), "System type", parts.systemType || "—"],
          ["Returned parts list", (returnedParts || []).join(", ") || "—", "RMA Ref", parts.rmaRef || "—"],
        ],
      },
    });

    if (f.internalNotes) {
      sections.push({
        title: "Warehouse / Engineer Notes",
        text: f.internalNotes,
      });
    }

    sections.push({
      title: "Office Notes (Internal)",
      text: row.adminNotes || "—",
      textStyle: { borderColor: [220, 0, 0], textColor: [220, 0, 0] },
    });
  } else {
    const t = row.parts?.templateSnapshot || {};
    const test = row.parts?.test || {};

    sections.push({
      title: "Return & Part",
      table: {
        rows: [
          ["Returns Note #", f.returnsNoteNumber, "Serial", f.serialNumber],
          ["SRET #", f.sretNumber, "Part Code", f.partCode],
          ["Part Title", f.partTitle || t.title, "Part Desc", (f.partDescription || t.description || "—").slice(0, 48)],
        ],
      },
    });

    sections.push({
      title: "Reported / Fault Found",
      table: {
        rows: [["Reported", f.reportedCode, "Fault Found", f.faultFoundCode]],
      },
    });

    if ((t.steps || []).length) {
      const rows = (t.steps || []).map((st) => {
        const v = test[st.id];
        const shown = st.type === "check" ? (v ? "Yes" : "No") : String(v || "—");
        return [st.label, shown, st.required ? "Required" : "", ""];
      });

      sections.push({
        title: "Test Procedure",
        table: { rows },
      });
    }


    if (f.internalNotes) {
      sections.push({
        title: "Warehouse / Engineer Notes",
        text: f.internalNotes,
      });
    }

    sections.push({
      title: "Office Notes (Internal)",
      text: row.adminNotes || "—",
      textStyle: { borderColor: [220, 0, 0], textColor: [220, 0, 0] },
    });
  }

  // Images
  if (row.storage?.folderId) {
    const images = await listImagesForReturnsV2(row);
    const take = (opts.maxImages ?? 12);
    const selected = (images || []).slice(0, take);

    const embedded = [];
    for (const img of selected) {
      try {
        const dataUrl = await getImageDataUrl(img.id);
        if (dataUrl) embedded.push({ dataUrl, format: dataUrl.includes("png") ? "PNG" : "JPEG", label: img.name || "" });
      } catch (e) {
        // ignore individual image failures
      }
    }

    if (embedded.length) {
      sections.push({
        title: "Photos",
        images: embedded,
        imagesGrid: { cols: 3, size: 150, gap: 10 },
      });
    }
  }

  return {
    title: isCyl ? "Cylinder Return Report" : "Parts Return Report",
    fileName: reportFileNameFromReturnsV2(row),
    subtitles,
    sections,
    header: opts.header || null,
    layout: { marginLeft: 40, marginRight: 40, startY: 50 },
  };
}

export async function pdfDownloadFromSpec(spec) {
  return downloadPdfFromSpec(spec);
}
